import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface NewFeedbackDto {
  Score?: number | null;
  BooleanScore?: boolean | null;
  Comment?: string | null;
  Data?: Record<string, any> | null;
  Files?: Record<string, string> | null;
}

export interface UpdateFeedbackDto {
  Score?: number | null;
  BooleanScore?: boolean | null;
  Comment?: string | null;
  Data?: Record<string, any> | null;
}

export interface FeedbackContext {
  IntegrationId: string;
  ProcessId: string;
}

export interface Feedback {
  Id: string;
  TenantId: string;
  FlowId: string;
  Score: number | null;
  BooleanScore: boolean | null;
  Comment: string | null;
  Data: Record<string, any> | null;
  StorageItems: Record<string, string> | null;
  Context: FeedbackContext;
  CreatedAt?: string;
  UpdatedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {
  private api: AxiosInstance | null = null;
  private platformId = inject(PLATFORM_ID);
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.api = axios.create({
        headers: {
          'Content-Type': 'application/json'
        }
      });

      this.api.interceptors.request.use((config) => {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/${tenantId}`;

        const token = this.authService.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      });
    }
  }

  async getAllFeedback(): Promise<Feedback[] | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get('/feedback');
      return response.data;
    } catch (error) {
      console.error('Failed to get all feedback:', error);
      throw error;
    }
  }

  async getFeedback(id: string): Promise<Feedback | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.get(`/feedback/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get feedback:', error);
      throw error;
    }
  }

  async addFeedback(
    integrationId: string,
    processId: string,
    flowId: string,
    feedback: NewFeedbackDto
  ): Promise<any | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.post(
        `/${integrationId}/feedback/${processId}/flow/${flowId}`,
        feedback
      );
      return response.data;
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error;
    }
  }

  async updateFeedback(id: string, feedback: UpdateFeedbackDto): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.put(`/feedback/${id}`, feedback);
      return response;
    } catch (error) {
      console.error('Failed to update feedback:', error);
      throw error;
    }
  }

  async deleteFeedback(id: string): Promise<AxiosResponse | null> {
    if (!this.api) return null;

    try {
      const response = await this.api.delete(`/feedback/${id}`);
      return response;
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      throw error;
    }
  }
}
